/**
 * useNativeGPS
 * -----------------------------------------------------------------
 * Bridge for the Android wrapper's GPS story. There are three tiers:
 *
 *  1. RideGYTracker (our custom Java foreground-service plugin) — the
 *     preferred path. When present it takes a Bearer token via
 *     setSession() and keeps pinging /api/drivers/location from a
 *     persistent foreground service even after the phone screen locks.
 *     This is the free replacement we wrote after ripping out the paid
 *     transistorsoft plugin that was crash-looping the app.
 *
 *  2. @capacitor/geolocation — asks for the runtime location permission
 *     so the WebView's navigator.geolocation.watchPosition inherits the
 *     grant. Used as a fallback when RideGYTracker isn't compiled in
 *     (e.g. old APKs that predate this hook).
 *
 *  3. Stock browser — the hook's methods no-op; the web app's own
 *     navigator.geolocation.watchPosition is the only GPS source.
 *
 * The hook exposes `isNative`, `tracking`, `permissionDenied`,
 * `startTracking`, `stopTracking` so callers don't care which tier is
 * doing the work.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { API } from "@/lib/api";

function nativeDisabled() {
  try {
    if (typeof window === "undefined") return false;
    const p = new URLSearchParams(window.location.search);
    if (p.get("nonative") === "1") {
      window.localStorage.setItem("ridegy_disable_native_gps", "1");
    }
    if (p.get("nonative") === "0") {
      window.localStorage.removeItem("ridegy_disable_native_gps");
    }
    return window.localStorage.getItem("ridegy_disable_native_gps") === "1";
  } catch { return false; }
}

function getRideGYTracker() {
  try { return window?.Capacitor?.Plugins?.RideGYTracker || null; }
  catch { return null; }
}
function getCapacitorGeolocation() {
  try { return window?.Capacitor?.Plugins?.Geolocation || null; }
  catch { return null; }
}

export function useNativeGPS({ sessionToken } = {}) {
  const isNative = typeof window !== "undefined"
    && !!window?.Capacitor?.isNativePlatform
    && !nativeDisabled();

  const [tracking, setTracking] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);

  // Hand the driver's Bearer token to the native side so
  // LocationForegroundService can attach it to POST /drivers/location.
  useEffect(() => {
    if (!isNative || !sessionToken) return;
    const tracker = getRideGYTracker();
    if (!tracker?.setSession) return;
    try {
      const origin = new URL(API).origin;
      tracker.setSession({ token: sessionToken, apiOrigin: origin })
        .catch((e) => console.warn("[useNativeGPS] setSession failed:", e?.message || e));
    } catch { /* invalid API url — noop */ }
  }, [isNative, sessionToken]);

  const startTracking = useCallback(async () => {
    if (!isNative) return { ok: false, reason: "not_native" };
    // Step 1: make sure we have the runtime location permission. Use
    // @capacitor/geolocation for the prompt because Android needs the
    // grant BEFORE FusedLocationProviderClient can subscribe.
    const geo = getCapacitorGeolocation();
    if (geo) {
      try {
        let perm = await geo.checkPermissions();
        if (perm?.location !== "granted") {
          perm = await geo.requestPermissions({ permissions: ["location"] });
        }
        if (perm?.location !== "granted") {
          setPermissionDenied(true);
          return { ok: false, reason: "permission_denied" };
        }
      } catch (e) {
        console.warn("[useNativeGPS] permission check failed:", e?.message || e);
      }
    }
    // Step 2: kick off our foreground service if the plugin is here.
    const tracker = getRideGYTracker();
    if (tracker?.start) {
      try {
        await tracker.start();
        setTracking(true);
        return { ok: true, backend: "ridegy_tracker" };
      } catch (e) {
        console.warn("[useNativeGPS] tracker.start failed:", e?.message || e);
      }
    }
    // No custom tracker — permission was granted so the web watchPosition
    // path will still work while the app is in the foreground.
    setTracking(true);
    return { ok: true, backend: "webview_geolocation" };
  }, [isNative]);

  const stopTracking = useCallback(async () => {
    if (!isNative) return { ok: false, reason: "not_native" };
    const tracker = getRideGYTracker();
    if (tracker?.stop) {
      try { await tracker.stop(); }
      catch (e) { console.warn("[useNativeGPS] tracker.stop failed:", e?.message || e); }
    }
    setTracking(false);
    return { ok: true };
  }, [isNative]);

  // Reflect existing permission state on cold-start.
  useEffect(() => {
    if (!isNative) return;
    (async () => {
      try {
        const geo = getCapacitorGeolocation();
        if (!geo) return;
        const perm = await geo.checkPermissions();
        if (perm?.location === "granted") setTracking(true);
      } catch { /* ignore */ }
    })();
  }, [isNative]);

  return useMemo(() => ({
    isNative,
    tracking,
    permissionDenied,
    startTracking,
    stopTracking,
  }), [isNative, tracking, permissionDenied, startTracking, stopTracking]);
}
